import React, { useState } from "react";
import {
  MessageSquare,
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Youtube,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useContactUs } from "@/api/mutation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { ContactUs } from "@/api/types";

// Define the form schema using zod
const contactFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().optional(),
  subject: z.string().min(1, "Subject is required"),
  message: z.string().min(1, "Message is required"),
  agreeToTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the privacy policy",
  }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export const Contact: React.FC = () => {
  const { mutate, isPending } = useContactUs();
  // Form submission state
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  // Initialize react-hook-form
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      subject: "",
      message: "",
      agreeToTerms: false,
    },
  });

  // Handle form submission
  const onSubmit = (values: ContactFormValues) => {
    setFormStatus("submitting");

    // Create payload for the API
    const contactPayload: ContactUs = {
      name: values.name.trim(),
      email: values.email.trim(),
      phoneNumber: values.phoneNumber?.trim() || "",
      subject: values.subject,
      message: values.message.trim(),
    };

    // Call the API using the mutation hook
    mutate(contactPayload, {
      onSuccess: () => {
        setFormStatus("success");
        // Reset form data on success
        form.reset();
      },
      onError: () => {
        setFormStatus("error");
      },
    });
  };

  return (
    <div className='max-w-7xl mx-auto px-6 py-12'>
      {/* Page Header */}
      <div className='mb-10 text-center'>
        <div className='inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-4'>
          <MessageSquare className='h-8 w-8 text-blue-600' />
        </div>
        <h1 className='text-3xl font-bold text-gray-900 mb-2'>Contact Us</h1>
        <p className='text-lg text-gray-600 max-w-2xl mx-auto'>
          Have questions or need assistance? We're here to help! Choose the option that works best
          for you.
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-10'>
        {/* Contact Information */}
        <div className='lg:col-span-1 space-y-6'>
          <Card className='p-6 border-0 shadow-sm'>
            <h2 className='text-xl font-bold text-gray-900 mb-6'>Get in Touch</h2>

            <div className='space-y-4'>
              <div className='flex items-start'>
                <div className='flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3'>
                  <Mail className='h-5 w-5 text-blue-600' />
                </div>
                <div>
                  <h3 className='text-sm font-medium text-gray-900'>Email Us</h3>
                  <p className='text-sm text-gray-500 mt-1'>We'll respond within 24 hours</p>
                  <a
                    href='mailto:support@yourcompany.com'
                    className='text-blue-600 hover:text-blue-800 font-medium block mt-1'
                  >
                    support@yourcompany.com
                  </a>
                </div>
              </div>

              <div className='flex items-start'>
                <div className='flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3'>
                  <Phone className='h-5 w-5 text-green-600' />
                </div>
                <div>
                  <h3 className='text-sm font-medium text-gray-900'>Call Us</h3>
                  <p className='text-sm text-gray-500 mt-1'>Mon-Fri, 9am-5pm WAT</p>
                  <a
                    href='tel:+2348012345678'
                    className='text-blue-600 hover:text-blue-800 font-medium block mt-1'
                  >
                    +234 801 234 5678
                  </a>
                </div>
              </div>

              <div className='flex items-start'>
                <div className='flex-shrink-0 h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center mr-3'>
                  <MapPin className='h-5 w-5 text-amber-600' />
                </div>
                <div>
                  <h3 className='text-sm font-medium text-gray-900'>Visit Us</h3>
                  <p className='text-sm text-gray-500 mt-1'>Our headquarters</p>
                  <address className='not-italic text-sm text-gray-700 mt-1'>
                    123 Business Avenue
                    <br />
                    Victoria Island
                    <br />
                    Lagos, Nigeria
                  </address>
                </div>
              </div>

              <div className='flex items-start'>
                <div className='flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3'>
                  <Clock className='h-5 w-5 text-purple-600' />
                </div>
                <div>
                  <h3 className='text-sm font-medium text-gray-900'>Business Hours</h3>
                  <table className='text-sm text-gray-700 mt-1'>
                    <tbody>
                      <tr>
                        <td className='pr-4'>Monday - Friday:</td>
                        <td>9:00 AM - 5:00 PM</td>
                      </tr>
                      <tr>
                        <td className='pr-4'>Saturday:</td>
                        <td>10:00 AM - 2:00 PM</td>
                      </tr>
                      <tr>
                        <td className='pr-4'>Sunday:</td>
                        <td>Closed</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Card>

          {/* Social Media */}
          <Card className='p-6 border-0 shadow-sm'>
            <h2 className='text-xl font-bold text-gray-900 mb-6'>Connect With Us</h2>

            <div className='flex space-x-4'>
              <a
                href='#'
                className='h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-colors'
                aria-label='Facebook'
              >
                <Facebook className='h-5 w-5' />
              </a>

              <a
                href='#'
                className='h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-pink-100 hover:text-pink-600 transition-colors'
                aria-label='Instagram'
              >
                <Instagram className='h-5 w-5' />
              </a>

              <a
                href='#'
                className='h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-sky-100 hover:text-sky-600 transition-colors'
                aria-label='Twitter'
              >
                <Twitter className='h-5 w-5' />
              </a>

              <a
                href='#'
                className='h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-blue-100 hover:text-blue-800 transition-colors'
                aria-label='LinkedIn'
              >
                <Linkedin className='h-5 w-5' />
              </a>

              <a
                href='#'
                className='h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-red-100 hover:text-red-600 transition-colors'
                aria-label='YouTube'
              >
                <Youtube className='h-5 w-5' />
              </a>
            </div>
          </Card>
        </div>

        {/* Contact Form */}
        <div className='lg:col-span-2'>
          <Card className='p-6 border-0 shadow-sm'>
            <h2 className='text-xl font-bold text-gray-900 mb-6 flex items-center'>
              <Send className='h-5 w-5 mr-2 text-blue-600' />
              Send Us a Message
            </h2>

            {formStatus === "success" ? (
              <div className='rounded-lg bg-green-50 p-6 text-center'>
                <CheckCircle className='h-12 w-12 text-green-500 mx-auto mb-4' />
                <h3 className='text-lg font-medium text-gray-900 mb-2'>
                  Message Sent Successfully!
                </h3>
                <p className='text-gray-600 mb-4'>
                  Thank you for reaching out. Our team will get back to you as soon as possible.
                </p>
                <Button
                  onClick={() => setFormStatus("idle")}
                  className='bg-green-600 hover:bg-green-700'
                >
                  Send Another Message
                </Button>
              </div>
            ) : formStatus === "error" ? (
              <div className='rounded-lg bg-red-50 p-6 text-center'>
                <AlertCircle className='h-12 w-12 text-red-500 mx-auto mb-4' />
                <h3 className='text-lg font-medium text-gray-900 mb-2'>Something Went Wrong</h3>
                <p className='text-gray-600 mb-4'>
                  We couldn't send your message. Please try again or use one of our other contact
                  methods.
                </p>
                <Button
                  onClick={() => setFormStatus("idle")}
                  className='bg-red-600 hover:bg-red-700'
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <Form.Root {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    {/* Name */}
                    <Form.Field
                      control={form.control}
                      name='name'
                      render={({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Your Name <span className='text-red-500'>*</span>
                          </Form.Label>
                          <Form.Control>
                            <Input
                              placeholder='John Doe'
                              autoComplete='name'
                              className='w-full'
                              {...field}
                            />
                          </Form.Control>
                          <Form.Message />
                        </Form.Item>
                      )}
                    />

                    {/* Email */}
                    <Form.Field
                      control={form.control}
                      name='email'
                      render={({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Email Address <span className='text-red-500'>*</span>
                          </Form.Label>
                          <Form.Control>
                            <Input
                              type='email'
                              placeholder='your@email.com'
                              autoComplete='email'
                              className='w-full'
                              {...field}
                            />
                          </Form.Control>
                          <Form.Message />
                        </Form.Item>
                      )}
                    />

                    {/* Phone */}
                    <Form.Field
                      control={form.control}
                      name='phoneNumber'
                      render={({ field }) => (
                        <Form.Item>
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control>
                            <Input
                              type='tel'
                              placeholder='+234 800 000 0000'
                              autoComplete='tel'
                              className='w-full'
                              {...field}
                            />
                          </Form.Control>
                          <Form.Message />
                        </Form.Item>
                      )}
                    />

                    {/* Subject */}
                    <Form.Field
                      control={form.control}
                      name='subject'
                      render={({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Subject <span className='text-red-500'>*</span>
                          </Form.Label>
                          <Form.Control>
                            <select
                              className='w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                              {...field}
                            >
                              <option value=''>Select a subject</option>
                              <option value='order-inquiry'>Order Inquiry</option>
                              <option value='shipping-delivery'>Shipping & Delivery</option>
                              <option value='returns-refunds'>Returns & Refunds</option>
                              <option value='product-inquiry'>Product Inquiry</option>
                              <option value='technical-support'>Technical Support</option>
                              <option value='feedback'>Feedback & Suggestions</option>
                              <option value='partnership'>Partnership Opportunities</option>
                              <option value='other'>Other</option>
                            </select>
                          </Form.Control>
                          <Form.Message />
                        </Form.Item>
                      )}
                    />
                  </div>

                  {/* Message */}
                  <Form.Field
                    control={form.control}
                    name='message'
                    render={({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Message <span className='text-red-500'>*</span>
                        </Form.Label>
                        <Form.Control>
                          <Textarea
                            className='w-full h-40'
                            placeholder='How can we help you?'
                            {...field}
                          />
                        </Form.Control>
                        <Form.Message />
                      </Form.Item>
                    )}
                  />

                  {/* Terms Agreement */}
                  <Form.Field
                    control={form.control}
                    name='agreeToTerms'
                    render={({ field }) => (
                      <Form.Item className='flex flex-row items-start space-y-0 space-x-2 p-0'>
                        <Form.Control>
                          <input
                            type='checkbox'
                            className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1'
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        </Form.Control>
                        <div className='space-y-1 leading-none'>
                          <Form.Label className='ml-2 block text-sm text-gray-700'>
                            I agree to the{" "}
                            <a href='#' className='text-blue-600 hover:underline'>
                              Privacy Policy
                            </a>{" "}
                            and consent to having my data processed to respond to my inquiry.
                          </Form.Label>
                          <Form.Message />
                        </div>
                      </Form.Item>
                    )}
                  />

                  {/* Submit Button */}
                  <div>
                    <Button
                      type='submit'
                      className='w-full bg-blue-600 hover:bg-blue-700 py-3 h-auto text-base font-medium'
                      disabled={formStatus === "submitting" || isPending}
                    >
                      {formStatus === "submitting" || isPending ? (
                        <span className='flex items-center justify-center'>
                          <svg
                            className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                          >
                            <circle
                              className='opacity-25'
                              cx='12'
                              cy='12'
                              r='10'
                              stroke='currentColor'
                              strokeWidth='4'
                            ></circle>
                            <path
                              className='opacity-75'
                              fill='currentColor'
                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                            ></path>
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        <span className='flex items-center justify-center'>
                          <Send className='mr-2 h-4 w-4' />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </div>
                </form>
              </Form.Root>
            )}
          </Card>
        </div>
      </div>

      {/* Map Section */}
      <div className='mt-12'>
        <Card className='p-6 border-0 shadow-sm'>
          <h2 className='text-xl font-bold text-gray-900 mb-6 flex items-center'>
            <MapPin className='h-5 w-5 mr-2 text-blue-600' />
            Find Us
          </h2>

          <div className='relative h-96 rounded-lg overflow-hidden'>
            {/* This is a placeholder for an actual map, which would typically use a library like Google Maps */}
            <div className='absolute inset-0 bg-gray-200 flex items-center justify-center'>
              <p className='text-gray-600'>
                Map would be displayed here. Integrate with Google Maps or similar service.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Footer with additional contact info */}
      <div className='mt-8 text-center text-sm text-gray-500'>
        <p>Our customer service team is available Monday to Friday, 9:00 AM to 5:00 PM (WAT).</p>
        <p className='mt-2'>
          For urgent inquiries outside business hours, please email{" "}
          <a href='mailto:urgent@yourcompany.com' className='text-blue-600 hover:underline'>
            urgent@yourcompany.com
          </a>
          .
        </p>
      </div>
    </div>
  );
};
